import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CLoseIcon from "../../../icons/close-icon.svg";
import { showSpinner, closeSpinner, modal, confirm } from "../../../js/modules/Dialogs";
import { setUser } from "../../../services/adminService";
import ReactSelect from "react-select";

import { sessionExpired, adminSaveChangesConfirm, adminMavCannotPreactivate, adminMavCannotReset } from "../../../config/messages";
import { toScreen } from "../../../js/modules/Routing";
import {
  BellIcon,
  BellSlashIcon,
  CloseIcon,
  DiscountIcon,
  EmailIcon,
  GeneralVanIcon,
  GoodsIcon,
  PublicIcon,
} from "../../commonUI/Icons/SvgIcon";
import { toast } from "react-toastify";
import RangeSlider from "../../commonUI/RangePicker";

const MavEdit = ({ view, mav, onClose, props }) => {
  const isEditProfile = view === "editprofile";
  const isEditAddress = view === "editaddress";
  const isEditVan = view === "editvan";
  const isEditCompany = view === "editcompany";
  const isEditInsurance = view === "editinsurance";
  const isEditNotifications = view === "editnotifications";
  const navigate = useNavigate();

  // Profile form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    phone: "",
    tshirtSize: "",
    drivingLicence: "",
    licenceCheckCode: "",
    termsAgreed: false,
    vanStickers: false,
    status: "",
  });

  // Address form state
  const [addressData, setAddressData] = useState({
    baseLocation: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    county: "",
    postcode: "",
    radius: 15,
  });

  // Van form state
  const [vanData, setVanData] = useState({
    vanMake: "",
    vanModel: "",
    vanYear: "",
    vehicleRegNumber: "",
  });

  // Company form state
  const [companyData, setCompanyData] = useState({
    companyName: "",
    companyNumber: "",
    vatNumber: "",
  });

  // Insurance state
  const [insuranceData, setInsuranceData] = useState({
    publicLiability: false,
    goodsinTransit: false,
    generalVanInsurance: false,
  });

  // Notification settings state
  const [settings, setSettings] = useState({
    emailnotifications: false,
    holidaymode: false,
    marketingnotifications: false,
    pushnotifications: false,
  });

  // Initialize form data when mav prop changes
  useEffect(() => {
    if (mav) {
      setFormData({
        firstName: mav?.accountInfo?.firstname || "",
        lastName: mav?.accountInfo?.surname || "",
        username: mav?.accountInfo?.username || "",
        email: mav?.accountInfo?.email || "",
        phone: mav?.accountInfo?.telephone || "",
        tshirtSize: mav?.tshirtSize || "",
        drivingLicence: mav?.accountInfo?.drivingLicence || "",
        licenceCheckCode: mav?.accountInfo?.licenceCheckCode || "",
        termsAgreed: mav?.accountInfo?.terms || false,
        vanStickers: mav?.vanStickers || false,
        status: mav?.status || "",

      });

      // Address data
      setAddressData({
        baseLocation:
          mav?.accountInfo?.baseLocation?.homeaddress?.formattedAddress || "",
        addressLine1:
          mav?.accountInfo?.baseLocation?.homeaddress?.addressLine1 || "",
        addressLine2:
          mav?.accountInfo?.baseLocation?.homeaddress?.addressLine2 || "",
        city: mav?.accountInfo?.baseLocation?.homeaddress?.cityName || "",
        county: mav?.accountInfo?.baseLocation?.homeaddress?.countyName || "",
        postcode: mav?.accountInfo?.baseLocation?.homeaddress?.postCode || "",
        radius: mav?.accountInfo?.baseLocation?.radius || 15,
      });

      // Van data
      setVanData({
        vanMake: mav?.vanMake || "",
        vanModel: mav?.vanModel || "",
        vanYear: mav?.vanYear || "",
        vehicleRegNumber: mav?.vanRegNumber || "",
      });

      // Company data
      setCompanyData({
        companyName: mav?.accountInfo?.companyName || "",
        companyNumber: mav?.accountInfo?.companyNumber || "",
        vatNumber: mav?.accountInfo?.vatNumber || "",
      });

      // Insurance data
      setInsuranceData({
        publicLiability: mav?.accountInfo?.publicLiabilityInsurance || false,
        goodsinTransit: mav?.accountInfo?.goodsInTransitInsurance || false,
        generalVanInsurance: mav?.accountInfo?.generalVanInsurance || false,
      });

      // Notification settings
      setSettings({
        emailnotifications:
          mav?.accountInfo?.settings?.emailnotifications || false,
        holidaymode: mav?.accountInfo?.settings?.holidaymode || false,
        marketingnotifications:
          mav?.accountInfo?.settings?.marketingnotifications || false,
        pushnotifications:
          mav?.accountInfo?.settings?.pushnotifications || false,
      });
    }
  }, [mav]);

  const option = [
    { value: "preactivation", label: "Pending" },
    { value: "active", label: "Active" },
    { value: "blocked", label: "Blocked" },
    { value: "delete", label: "Delete" },
  ];


  // Handle ReactSelect change
  const handleSelectChange = (selectedOption, stateType = "form") => {
    if (selectedOption) {
      switch (stateType) {
        case "form":
          setFormData((prev) => ({
            ...prev,
            [selectedOption.name]: selectedOption.value,
          }));
          break;
        case "insurance":
          setInsuranceData((prev) => ({
            ...prev,
            [selectedOption.name]: selectedOption.value,
          }));
          break;
      }
    }
  };

  // Status select change handler
  const handleStatusChange = (selectedOption) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        status: selectedOption.value,
      }));
    }
  };

  // Generic input change handler
  const handleInputChange = (event, stateType = "form") => {
    const { name, value, type, checked } = event.target;
    const inputValue = type === "checkbox" ? checked : value;

    switch (stateType) {
      case "form":
        setFormData((prev) => ({ ...prev, [name]: inputValue }));
        break;
      case "address":
        setAddressData((prev) => ({ ...prev, [name]: inputValue }));
        break;
      case "van":
        setVanData((prev) => ({ ...prev, [name]: inputValue }));
        break;
      case "company":
        setCompanyData((prev) => ({ ...prev, [name]: inputValue }));
        break;
      case "insurance":
        setInsuranceData((prev) => ({ ...prev, [name]: inputValue }));
        break;
      case "settings":
        setSettings((prev) => ({ ...prev, [name]: inputValue }));
        break;
    }
  };

  // API response handler
  const handleSaveResponse = useCallback(
    (resStatus, responseString, resJson) => {
      closeSpinner();

      switch (resStatus) {
        case 401:
          modal(sessionExpired);
          toScreen(false, navigate, "/logout");
          break;
        case 400:
          modal(responseString);
          break;
        case 200:
          toast.success("Changes successfully saved");
          toScreen(false, navigate, "/mavs");
          break;
        default:
          modal("An error occurred. Please try again.");
      }
    },
    [navigate]
  );

  // Save mav data
  const handleSave = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }
  
      showSpinner();
  
      // Prepare userData based on the current view
      let updatedAccountInfo = { ...mav.accountInfo };
      let updatedMav = { ...mav };
  
      if (isEditProfile) {
        updatedAccountInfo = {
          ...updatedAccountInfo,
          username: formData?.username,
          firstname: formData?.firstName,
          surname: formData?.lastName,
          telephone: formData?.phone,
          email: formData?.email,
          drivingLicence: formData?.drivingLicence,
          licenceCheckCode: formData?.licenceCheckCode,
          terms: formData?.termsAgreed,
        };
        updatedMav = {
          ...updatedMav,
          tshirtSize: formData?.tshirtSize,
          vanStickers: formData?.vanStickers,
          status: formData?.status
        };
      }
  
      if (isEditAddress) {
        updatedAccountInfo = {
          ...updatedAccountInfo,
          baseLocation: {
            ...updatedAccountInfo.baseLocation,
            homeaddress: {
              ...updatedAccountInfo.baseLocation?.homeaddress,
              formattedAddress: addressData.baseLocation,
              addressLine1: addressData.addressLine1,
              addressLine2: addressData.addressLine2,
              cityName: addressData.city,
              countyName: addressData.county,
              postCode: addressData.postcode,
            },
            radius: addressData.radius,
          },
        };
      }
  
      if (isEditVan) {
        updatedAccountInfo = {
          ...updatedAccountInfo,
          vanDetails: {
            make: vanData?.vanMake,
            model: vanData?.vanModel,
            year: vanData?.vanYear,
            regNumber: vanData?.vehicleRegNumber,
          },
        };
      }
  
      if (isEditCompany) {
        updatedAccountInfo = {
          ...updatedAccountInfo,
          companyInfo: {
            name: companyData?.companyName,
            number: companyData?.companyNumber,
            vatNumber: companyData?.vatNumber,
          },
        };
      }
  
      if (isEditInsurance) {
        updatedAccountInfo = {
          ...updatedAccountInfo,
          publicLiabilityInsurance: insuranceData?.publicLiability,
          goodsInTransitInsurance: insuranceData?.goodsinTransit,
          generalVanInsurance: insuranceData?.generalVanInsurance,
        };
      }
  
      if (isEditNotifications) {
        updatedAccountInfo = {
          ...updatedAccountInfo,
          settings: settings,
        };
      }
  
      const userData = {
        accountInfo: {
          username: props?.userDetails?.accountInfo?.username,
          email: props?.userDetails?.accountInfo?.email,
        },
        user: {
          _id: mav._id,
          short_id: mav.short_id,
          notes: mav?.notes,
          accountInfo: updatedAccountInfo,
          userType: mav.userType,
          status: updatedMav.status
        },
      };
  
      setUser(userData, handleSaveResponse, props?.userDetails?.jwttoken);
    },
    [
      props?.userDetails,
      mav,
      handleSaveResponse,
      formData,
      addressData,
      vanData,
      companyData,
      insuranceData,
      settings,
      isEditProfile,
      isEditAddress,
      isEditVan,
      isEditCompany,
      isEditInsurance,
      isEditNotifications,
    ]
  );
  
  // tShirt options
  const tshirtSizeOptions = [
    { value: "xsmall", label: "XS" },
    { value: "small", label: "S" },
    { value: "medium", label: "M" },
    { value: "Large", label: "L" },
    { value: "xlarge", label: "XL" },
    { value: "xxlarge", label: "XXL" },
    { value: "xxxlarge", label: "XXXL" },
  ];

  // Insurance configuration
  const insuranceFields = [
    {
      name: "publicLiability",
      title: "Public Liability",
      icon: <PublicIcon />,
      description: "Insurance for third-party property damage or injury",
      checked: insuranceData?.publicLiability,
    },
    {
      name: "goodsinTransit",
      title: "Goods in Transit",
      icon: <GoodsIcon />,
      description: "Insurance for items being transported",
      checked: insuranceData?.goodsinTransit,
    },
    {
      name: "generalVanInsurance",
      title: "General Van Insurance",
      icon: <GeneralVanIcon />,
      description: "Standard van insurance with business use",
      checked: insuranceData?.generalVanInsurance,
    },
  ];

  // Notification configuration
  const notificationSettings = [
    {
      name: "pushnotifications",
      title: "Push notifications",
      icon: <BellIcon />,
      description: "User receives job alerts and updates on their devices",
      checked: settings.pushnotifications,
    },
    {
      name: "emailnotifications",
      title: "Email notifications",
      icon: <EmailIcon />,
      description: "User receives job alerts and updates via email",
      checked: settings.emailnotifications,
    },
    {
      name: "marketingnotifications",
      title: "Marketing notifications",
      icon: <DiscountIcon />,
      description: "User receives promotional and marketing updates",
      checked: settings.marketingnotifications,
    },
    {
      name: "holidaymode",
      title: "Holiday mode",
      icon: <BellSlashIcon />,
      description: "Pause all notifications while user is on holiday",
      checked: settings.holidaymode,
    },
  ];

  return (
    <div className="right-modal discount-modal">
      {/* Modal Header */}
      <div className="modal-header">
        <div className="modal-title">
          <h2>
            {isEditProfile && "Edit Profile"}
            {isEditAddress && "Edit Address"}
            {isEditVan && "Edit Van"}
            {isEditCompany && "Edit Company Information"}
            {isEditInsurance && "Edit Insurance Details"}
            {isEditNotifications && "Edit Notifications Settings"}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Modal Body */}
      <div className="modal-body">
        {isEditProfile && (
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label htmlFor="status" className="form-label">Status</label>

              <ReactSelect
                classNamePrefix="custom-select"
                options={option}
                name="status"
                placeholder="Select Status"
                value={option?.find(opt => opt.value === formData?.status)}
                onChange={handleStatusChange}
              />

            </div>
            <div className="form-group">
              <label htmlFor="firstName" className="form-label">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                className="form-control"
                placeholder="First Name"
                value={formData?.firstName}
                onChange={(e) => handleInputChange(e, "form")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName" className="form-label">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                className="form-control"
                placeholder="Last Name"
                value={formData?.lastName}
                onChange={(e) => handleInputChange(e, "form")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                className="form-control"
                placeholder="Username"
                value={formData?.username}
                onChange={(e) => handleInputChange(e, "form")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-control"
                placeholder="Email"
                value={formData?.email}
                onChange={(e) => handleInputChange(e, "form")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Telephone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                className="form-control"
                placeholder="Telephone"
                value={formData?.phone}
                onChange={(e) => handleInputChange(e, "form")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="drivingLicence" className="form-label">
                Driving Licence No
              </label>
              <input
                type="text"
                id="drivingLicence"
                name="drivingLicence"
                className="form-control"
                placeholder="Driving Licence No"
                value={formData?.drivingLicence}
                onChange={(e) => handleInputChange(e, "form")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="licenceCheckCode" className="form-label">
                Licence Check Code
              </label>
              <input
                type="text"
                id="licenceCheckCode"
                name="licenceCheckCode"
                className="form-control"
                placeholder="Licence Check Code"
                value={formData?.licenceCheckCode}
                onChange={(e) => handleInputChange(e, "form")}
              />
            </div>
            <div>
              <ul className="notification-list">
                <li className="notification-list-item notification-modal-card">
                  <div>
                    <div className="d-flex align-items-center g-8">
                      <span className="text-sm">Terms Agreed</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Driver has agreed to the terms and conditions
                    </p>
                  </div>
                  <label htmlFor="termsAgreed" className="switch">
                    <input
                      type="checkbox"
                      id="termsAgreed"
                      name="termsAgreed"
                      onChange={(e) => handleInputChange(e, "form")}
                      checked={formData?.termsAgreed || false}
                    />
                    <span className="slider"></span>
                  </label>
                </li>
              </ul>
              <br />
            </div>
            <ReactSelect
              classNamePrefix="custom-select"
              options={tshirtSizeOptions}
              name="tshirtSize"
              placeholder="Select Size"
              value={tshirtSizeOptions?.find((opt) => opt?.value === formData?.tshirtSize)}
              onChange={(selectedOption) => handleSelectChange(selectedOption, "form")}
            />
            <br />
            <div>
              <ul className="notification-list">
                <li className="notification-list-item notification-modal-card">
                  <div>
                    <div className="d-flex align-items-center g-8">
                      <span className="text-sm">Needs Van Stickers</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Driver needs branded stickers for their van
                    </p>
                  </div>
                  <label htmlFor="vanStickers" className="switch">
                    <input
                      id="vanStickers"
                      name="vanStickers"
                      type="checkbox"
                      onChange={(e) => handleInputChange(e, "form")}
                      checked={formData?.vanStickers || false}
                    />
                    <span className="slider"></span>
                  </label>
                </li>
              </ul>
            </div>
          </form>
        )}

        {isEditAddress && (
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label htmlFor="baseLocation" className="form-label">
                Base Location
              </label>
              <input
                type="text"
                id="baseLocation"
                name="baseLocation"
                className="form-control"
                placeholder="Base location"
                value={addressData.baseLocation}
                onChange={(e) => handleInputChange(e, "address")}
              />
              Radius around your base location where jobs will be shown


            </div>
            <RangeSlider
              value={addressData?.radius}
              onChange={(newValue) =>
                setAddressData((prev) => ({ ...prev, radius: newValue }))
              }
              min={0}
              max={1000}
            />
            <br />
            {/* <div className="form-group">
              <label className="form-label">Working Radius</label>
              <div className="form-control">{addressData?.radius} miles</div>
            </div> */}
            <div className="form-group">
              <label htmlFor="addressLine1" className="form-label">
                Address Line 1
              </label>
              <input
                type="text"
                id="addressLine1"
                name="addressLine1"
                className="form-control"
                placeholder="Address Line 1"
                value={addressData.addressLine1}
                onChange={(e) => handleInputChange(e, "address")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="addressLine2" className="form-label">
                Address Line 2
              </label>
              <input
                type="text"
                id="addressLine2"
                name="addressLine2"
                className="form-control"
                placeholder="Address Line 2"
                value={addressData.addressLine2}
                onChange={(e) => handleInputChange(e, "address")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="city" className="form-label">
                City
              </label>
              <input
                type="text"
                id="city"
                name="city"
                className="form-control"
                placeholder="City"
                value={addressData.city}
                onChange={(e) => handleInputChange(e, "address")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="county" className="form-label">
                County
              </label>
              <input
                type="text"
                id="county"
                name="county"
                className="form-control"
                placeholder="County"
                value={addressData.county}
                onChange={(e) => handleInputChange(e, "address")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="postcode" className="form-label">
                Postcode
              </label>
              <input
                type="text"
                id="postcode"
                name="postcode"
                className="form-control"
                placeholder="Postcode"
                value={addressData.postcode}
                onChange={(e) => handleInputChange(e, "address")}
              />
            </div>
          </form>
        )}

        {isEditVan && (
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label htmlFor="vanMake" className="form-label">
                Van Make
              </label>
              <input
                type="text"
                id="vanMake"
                name="vanMake"
                className="form-control"
                placeholder="Van Make"
                value={vanData.vanMake}
                onChange={(e) => handleInputChange(e, "van")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="vanModel" className="form-label">
                Van Model
              </label>
              <input
                type="text"
                id="vanModel"
                name="vanModel"
                className="form-control"
                placeholder="Van Model"
                value={vanData.vanModel}
                onChange={(e) => handleInputChange(e, "van")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="vanYear" className="form-label">
                Van Year
              </label>
              <input
                type="text"
                id="vanYear"
                name="vanYear"
                className="form-control"
                placeholder="Van Year"
                value={vanData.vanYear}
                onChange={(e) => handleInputChange(e, "van")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="vehicleRegNumber" className="form-label">
                Vehicle Reg. Number
              </label>
              <input
                type="text"
                id="vehicleRegNumber"
                name="vehicleRegNumber"
                className="form-control"
                placeholder="Vehicle Reg. Number"
                value={vanData.vehicleRegNumber}
                onChange={(e) => handleInputChange(e, "van")}
              />
            </div>
          </form>
        )}

        {isEditCompany && (
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label htmlFor="companyName" className="form-label">
                Company Name
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                className="form-control"
                placeholder="Company Name"
                value={companyData.companyName}
                onChange={(e) => handleInputChange(e, "company")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="companyNumber" className="form-label">
                Company Number
              </label>
              <input
                type="text"
                id="companyNumber"
                name="companyNumber"
                className="form-control"
                placeholder="Company Number"
                value={companyData.companyNumber}
                onChange={(e) => handleInputChange(e, "company")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="vatNumber" className="form-label">
                VAT Number
              </label>
              <input
                type="text"
                id="vatNumber"
                name="vatNumber"
                className="form-control"
                placeholder="VAT Number"
                value={companyData.vatNumber}
                onChange={(e) => handleInputChange(e, "company")}
              />
            </div>
          </form>
        )}

        {isEditInsurance && (
          <ul className="notification-list">
            {insuranceFields?.map(
              ({ icon, name, title, description, checked }) => (
                <li
                  className="notification-list-item notification-modal-card"
                  key={name}
                >
                  <div>
                    <div className="d-flex align-items-center g-8">
                      {icon}
                      <span className="text-sm">{title}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">{description}</p>
                  </div>
                  <label htmlFor={name} className="switch">
                    <input
                      id={name}
                      name={name}
                      type="checkbox"
                      onChange={(e) => handleInputChange(e, "insurance")}
                      checked={checked}
                    />
                    <span className="slider"></span>
                  </label>
                </li>
              )
            )}
          </ul>
        )}

        {isEditNotifications && (
          <ul className="notification-list">
            {notificationSettings?.map(
              ({ icon, name, title, description, checked }) => (
                <li
                  className="notification-list-item notification-modal-card"
                  key={name}
                >
                  <div>
                    <div className="d-flex align-items-center g-8">
                      {icon}
                      <span className="text-sm">{title}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">{description}</p>
                  </div>
                  <label htmlFor={name} className="switch">
                    <input
                      id={name}
                      name={name}
                      type="checkbox"
                      onChange={(e) => handleInputChange(e, "settings")}
                      checked={checked}
                    />
                    <span className="slider"></span>
                  </label>
                </li>
              )
            )}
          </ul>
        )}
      </div>

      {/* Modal Footer */}
      <div className="modal-footer">
        <div className="d-flex align-items-center g-12">
          <button
            className="btn btn-primary"
            type="button"
            onClick={handleSave}
          >
            Save Changes
          </button>
          <button className="btn btn-outline-primary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MavEdit;
